import { Avatar, Badge, Button, Spinner } from "@heroui/react";
import { Icon } from "@iconify/react";
import {
	type AccessorKeyColumnDef,
	createColumnHelper,
} from "@tanstack/react-table";

import { Chip, ProgressBar } from "@onesolution/components";
import ChipList, { type Colors } from "@onesolution/components/ChipList";
import type { OperationDetailsTabs } from "@onesolution/components/OperationDrawer/OperationDetails";
import { StatusSelector } from "@onesolution/components/form/StatusSelector";
import { useUserInfos } from "@onesolution/hooks";
import type {
	TradOptions,
	TranslationsKeys,
} from "@onesolution/hooks/useLanguage";
import type { OperationsList } from "@onesolution/serverFn";
import { getOperationsSubitemsFromServer } from "@onesolution/serverFn/operations/getOperationsSubitems";
import { markNotifAsReadOnServer } from "@onesolution/serverFn/user/markNotifAsRead";
import { useServerFn } from "@tanstack/react-start";
import lodash from "lodash";
import { useState } from "react";
import { Assignees, Deadline, Name } from "./table/components";

const columnHelper = createColumnHelper<OperationsList>();

export function getColumnOperationTable(
	t: (key: TranslationsKeys, options?: TradOptions) => string,
	openDetails: (
		clickedOpId: string,
		defaultOpenedTab?: OperationDetailsTabs,
		onlyChat?: boolean,
	) => void,
	refreshRender: () => void,
): AccessorKeyColumnDef<OperationsList[number]>[] {
	const getOperationSubData = useServerFn(getOperationsSubitemsFromServer);
	const { mutate: markAsRead } = markNotifAsReadOnServer.useMutation();

	const user = useUserInfos();

	const nameColumn = columnHelper.accessor("name", {
		id: "name",
		header: ({ table }) => `${t("basic.name")} (${table.getRowCount()})`,
		cell: ({ renderValue, row }) => {
			const [isLoading, setIsLoading] = useState(false);
			const item = row.original as unknown as (typeof row.original)[number] & {
				subitems?: unknown[];
			};

			return (
				<div
					style={{ paddingLeft: `${row.depth * 2}rem` }}
					className="flex items-center justify-between gap-4 lg:max-w-72 xl:max-w-96 max-w-48"
				>
					{isLoading && <Spinner size="sm" className="mr-2" />}
					{row.getCanExpand() && !isLoading ? (
						<Button
							isIconOnly
							onPress={async () => {
								if (item.subitems === undefined) {
									setIsLoading(true);
									const subitems = await getOperationSubData({
										data: { depth: row.depth, itemIds: item.subitemIds || [] },
									});
									item.subitems = subitems;
									refreshRender();
									setIsLoading(false);
								}

								const baseHandler = row.getToggleExpandedHandler();

								return baseHandler();
							}}
							variant="light"
							className="min-w-6! w-6! mr-2 p-0!"
						>
							<Icon
								icon={
									row.getIsExpanded()
										? "solar:alt-arrow-down-outline"
										: "solar:alt-arrow-right-outline"
								}
								className="size-4"
							/>
						</Button>
					) : null}
					<Name name={(renderValue() as string) || ""} />
					{item.thumbnail && (
						<Avatar
							isBordered
							size="sm"
							radius="sm"
							className="w-12"
							src={item.thumbnail.url}
							showFallback={false}
						/>
					)}
				</div>
			);
		},
		size: 200,
		maxSize: 400,
	});

	const folderColumn = columnHelper.accessor("boxFolderId", {
		id: "boxFolderId",
		header: "",
		cell: ({ getValue }) => {
			const boxFolderId = getValue() as OperationsList[number]["boxFolderId"];

			return boxFolderId ? (
				<Button
					isIconOnly
					variant="light"
					onPress={() => {
						window.open(`/app/files?folderId=${boxFolderId}`, "_self");
					}}
				>
					<Icon icon="solar:folder-with-files-linear" />
				</Button>
			) : null;
		},
		maxSize: 50,
	});

	const messagesColumn = columnHelper.accessor("messagesCount", {
		id: "messagesCount",
		header: "",
		cell: ({ row }) => {
			const original = row.original as unknown as {
				id: OperationsList[number]["id"];
				hasUnreadNotif: OperationsList[number]["hasUnreadNotif"];
			};

			return (
				<Button
					isIconOnly
					variant="light"
					onPress={async (e) => {
						await markAsRead({ pulseId: original.id });
						openDetails(
							lodash.get(row, "original.id") as unknown as string,
							"activity",
							row.depth !== 0,
						);
					}}
				>
					<Badge
						color="danger"
						isDot
						content={" "}
						size="sm"
						hidden={!original.hasUnreadNotif}
					>
						<Icon icon="solar:chat-round-dots-line-duotone" />
					</Badge>
				</Button>
			);
		},
		maxSize: 50,
	});
	const statusColumn = columnHelper.accessor("status", {
		id: "status",
		header: t("basic.status"),
		cell: ({ getValue, row }) => {
			const status = getValue() as OperationsList[number]["status"];
			if (row.depth === 2) {
				const item = row.original as unknown as {
					canEditStatus: boolean;
					id: string;
				};
				if (item.canEditStatus)
					return <StatusSelector taskId={item.id} value={status.color} />;
			}

			return status.label ? (
				<Chip variant="dot" dotColor={status.color}>
					{status.label}
				</Chip>
			) : null;
		},
		maxSize: 120,
	});

	const assigneeColumn = columnHelper.accessor("assignees", {
		id: "assignees",
		header: t("basic.assignees"),
		cell: ({ getValue }) => {
			const assignees = getValue() as OperationsList[number]["assignees"];

			return <Assignees all={assignees} />;
		},
		size: 100,
		minSize: 100,
		maxSize: 100,
	});

	const deadlineColumn = columnHelper.accessor("deadline", {
		id: "deadline",
		header: t("basic.deadline"),
		cell: ({ cell }) => {
			const date = cell.getValue() as OperationsList[number]["deadline"];

			return <Deadline date={date} />;
		},
		size: 100,
		minSize: 100,
		maxSize: 100,
	});

	const progressColumn = columnHelper.accessor("progress", {
		id: "progress",
		header: t("basic.progress"),
		cell: ({ cell }) => {
			const value = cell.getValue() as OperationsList[number]["progress"];

			if (value.status.label)
				return (
					<Chip
						variant="flat"
						style={{ background: `${value.status.color}30` }}
					>
						{value.status.label}
					</Chip>
				);

			if (value.global.length === 1 && !value.global[0].status) return null;

			return (
				<div>
					<ProgressBar status={value.global || []} />
				</div>
			);
		},
		maxSize: 128,
	});

	const proofURLColumn = columnHelper.accessor("proof", {
		id: "proof",
		header: t("basic.proof"),
		cell: ({ cell }) => {
			const proof = cell.getValue() as OperationsList[number]["proof"];
			if (!proof?.link && !proof?.status) return null;

			if (!proof.status.label) return null;

			return (
				<div>
					<Chip variant="bordered" link={proof.link}>
						<div className="flex gap-2 items-center">
							<span
								className="h-2 w-2 rounded-full"
								style={{
									backgroundColor: proof.status.color,
								}}
							/>
							{t(`status.${proof.status.label}` as TranslationsKeys, {
								skeleton: false,
							})}
						</div>
					</Chip>
				</div>
			);
		},
		size: 100,
		minSize: 100,
		maxSize: 100,
	});

	const quotesURLColumn = columnHelper.accessor("quotes", {
		id: "quotes",
		header: t("basic.quotes"),
		cell: ({ cell }) => {
			const quote = cell.getValue() as OperationsList[number]["quotes"];
			if (!quote?.link && !quote?.status) return null;

			return (
				<div>
					<Chip variant="bordered" link={quote.link}>
						<div className="flex gap-2 items-center">
							<span
								className="h-2 w-2 rounded-full"
								style={{
									backgroundColor: quote.status.color,
								}}
							/>
							{t(`status.${quote.status.label}` as TranslationsKeys, {
								skeleton: false,
							})}
						</div>
					</Chip>
				</div>
			);
		},
		size: 100,
		minSize: 100,
		maxSize: 100,
	});

	const serviceColumn = columnHelper.accessor("services", {
		id: "service",
		header: `${t("name.service")}/${t("name.tags")}`,
		cell: ({ cell, row }) => {
			const services = cell.getValue() as OperationsList[number]["services"];
			const tags = (
				row.original as unknown as { tags: OperationsList[number]["tags"] }
			).tags;

			return (
				<ChipList
					chips={[
						...(services || []).map((s) => ({
							label: s.name,
							color: "secondary" as Colors,
							variant: user.favoriteServices.includes(s.id) ? "solid" : "flat",
						})),
						...(tags || []).map((s) => ({
							label: s,
							color: "default" as Colors,
						})),
					]}
				/>
			);
		},
		size: 100,
		minSize: 100,
		maxSize: 100,
	});

	return [
		nameColumn as AccessorKeyColumnDef<OperationsList[number]>,
		folderColumn as AccessorKeyColumnDef<OperationsList[number]>,
		messagesColumn as AccessorKeyColumnDef<OperationsList[number]>,
		statusColumn as AccessorKeyColumnDef<OperationsList[number]>,
		progressColumn as AccessorKeyColumnDef<OperationsList[number]>,
		deadlineColumn as AccessorKeyColumnDef<OperationsList[number]>,
		proofURLColumn as AccessorKeyColumnDef<OperationsList[number]>,
		quotesURLColumn as AccessorKeyColumnDef<OperationsList[number]>,
		serviceColumn as AccessorKeyColumnDef<OperationsList[number]>,
		assigneeColumn as AccessorKeyColumnDef<OperationsList[number]>,
	];
}
